import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { getBeatCollection } from "@/lib/bkls.functions";
import { BeatCard } from "@/components/beats/BeatCard";
import { beatCollectionsQuery, beatsCatalogQuery, beatsTiersQuery } from "@/lib/bkls-queries";
import { track } from "@/lib/analytics";
import { breadcrumbJsonLd } from "@/lib/bkls-rights";
import { absoluteUrl } from "@/lib/site";

const collectionQuery = (slug: string) =>
  queryOptions({
    queryKey: ["bkls", "collection", slug],
    queryFn: () => getBeatCollection({ data: { slug } }),
  });

export const Route = createFileRoute("/beats/collections/$slug")({
  loader: async ({ context, params }) => {
    const result = await context.queryClient.ensureQueryData(collectionQuery(params.slug));
    if (!result) throw notFound();
    await Promise.all([
      context.queryClient.ensureQueryData(beatsCatalogQuery),
      context.queryClient.ensureQueryData(beatsTiersQuery),
      context.queryClient.ensureQueryData(beatCollectionsQuery),
    ]);
    return result;
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Collection unavailable · Buddha KA" }, { name: "robots", content: "noindex" }],
      };
    }
    const { collection, beats } = loaderData;
    const url = absoluteUrl(`/beats/collections/${params.slug}`);
    const title = collection.seo_title || `${collection.name} · Beat Collection · Buddha KA`;
    const description =
      collection.seo_description ||
      collection.tagline ||
      `${collection.name} — ${beats.length} original Buddha KA productions available for licensing.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary" },
        // An empty collection stays out of the index until it holds beats.
        ...(beats.length === 0 ? [{ name: "robots", content: "noindex" }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbJsonLd([
              { name: "Beats", url: absoluteUrl("/beats") },
              { name: "Collections", url: absoluteUrl("/beats/collections") },
              { name: collection.name, url },
            ]),
          ),
        },
        ...(beats.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify({
                  "@context": "https://schema.org",
                  "@type": "CollectionPage",
                  name: collection.name,
                  description,
                  url,
                  mainEntity: {
                    "@type": "ItemList",
                    numberOfItems: beats.length,
                    itemListElement: beats.map((b, i) => ({
                      "@type": "ListItem",
                      position: i + 1,
                      name: b.title,
                      url: absoluteUrl(`/beats/${b.slug}`),
                    })),
                  },
                }),
              },
            ]
          : []),
      ],
    };
  },
  notFoundComponent: CollectionMissing,
  errorComponent: CollectionMissing,
  component: CollectionDetail,
});

function CollectionMissing() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
      <h1 className="font-serif text-4xl">That collection is not available.</h1>
      <Link to="/beats/collections" className="mt-6 inline-flex text-sm text-gold hover:text-gold-luminous">
        ← All collections
      </Link>
    </div>
  );
}

function CollectionDetail() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(collectionQuery(slug));
  const catalog = useQuery(beatsCatalogQuery);
  const tiers = useQuery(beatsTiersQuery);
  const allCollections = useQuery(beatCollectionsQuery);

  useEffect(() => {
    track("beat_collection_view", { slug });
  }, [slug]);

  if (!data) return <CollectionMissing />;
  const { collection } = data;

  // Cards come from the marketplace catalog so pricing, previews and metadata
  // stay identical to every other surface.
  const beats = (catalog.data ?? []).filter((b) => b.collectionSlug === slug);
  const siblings = (allCollections.data ?? []).filter((c) => c.slug !== slug && c.beatCount > 0).slice(0, 4);

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/beats" className="text-gold hover:text-gold-luminous">
          Beats
        </Link>{" "}
        /{" "}
        <Link to="/beats/collections" className="text-gold hover:text-gold-luminous">
          Collections
        </Link>{" "}
        / {collection.name}
      </nav>

      <h1 className="mt-4 font-serif text-4xl sm:text-5xl">{collection.name}</h1>
      {collection.tagline && <p className="mt-3 text-muted-foreground">{collection.tagline}</p>}
      {collection.description && (
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">{collection.description}</p>
      )}
      {beats.length > 0 && (
        <p className="mt-6 text-xs uppercase tracking-[0.2em] text-gold/80">
          {beats.length} {beats.length === 1 ? "beat" : "beats"} open for licensing
        </p>
      )}

      {catalog.isLoading ? (
        <p className="mt-12 text-sm text-muted-foreground">Loading the collection…</p>
      ) : beats.length === 0 ? (
        <div className="mt-12 rounded-2xl border hairline bg-charcoal p-8 text-center">
          <p className="font-serif text-2xl text-ivory">Nothing published here yet.</p>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            No beats have been released into this collection. Custom licensing is open in the meantime.
          </p>
          <Link
            to="/beats/custom"
            className="mt-6 inline-flex rounded-full border border-gold px-5 py-2.5 text-sm text-gold-luminous hover:bg-gold hover:text-obsidian"
          >
            Request a custom licence
          </Link>
        </div>
      ) : (
        <ul className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {beats.map((b) => (
            <li key={b.id}>
              <BeatCard
                beat={b}
                tiers={tiers.data ?? []}
                onSelect={() => track("beat_card_click", { slug: b.slug, section: `collection:${slug}` })}
              />
            </li>
          ))}
        </ul>
      )}

      {siblings.length > 0 && (
        <section className="mt-20" aria-labelledby="other-collections">
          <h2 id="other-collections" className="font-serif text-2xl text-gold-luminous">
            Other collections
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {siblings.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/beats/collections/$slug"
                  params={{ slug: c.slug }}
                  className="block rounded-2xl border hairline bg-charcoal p-5 transition-colors hover:border-gold"
                >
                  <h3 className="font-serif text-lg text-ivory">{c.name}</h3>
                  <p className="mt-2 text-xs uppercase tracking-[0.2em] text-gold/80">
                    {c.beatCount} {c.beatCount === 1 ? "beat" : "beats"}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link to="/beats/catalog" className="mt-12 inline-flex text-sm text-gold hover:text-gold-luminous">
        Browse the full catalog →
      </Link>
    </div>
  );
}
